import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon, { type IconName } from '../../components/Icon';
import { Banner, PageLoading } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { RepDashboard, RepTrendPoint } from '../../types';

/**
 * One salesman's performance in a screen — the manager's drill-down.
 * Opened from a team member; reads GET /reports/rep/:userId/dashboard,
 * whose scope the server enforces (a manager only their own team).
 */

const iso = (d: Date) => d.toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };
const today = () => iso(new Date());

function mins(m: number | null): string {
  if (m == null) return '—';
  const total = Math.round(m); const h = Math.floor(total / 60); const r = total % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}
function inr(paise: number): string {
  const r = paise / 100;
  if (r >= 1e5) return `₹${(r / 1e5).toFixed(r >= 1e6 ? 0 : 1)}L`;
  if (r >= 1000) return `₹${(r / 1000).toFixed(1)}k`;
  return `₹${Math.round(r).toLocaleString('en-IN')}`;
}

type Preset = 'today' | 'month' | 'custom';

export default function RepDashboardPage() {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [preset, setPreset] = useState<Preset>('month');
  const [data, setData] = useState<RepDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<RepDashboard>(`/reports/rep/${userId}/dashboard`, { from, to });
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the dashboard.');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, userId]);

  useEffect(() => { void run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'today') { setFrom(today()); setTo(today()); }
    else if (p === 'month') { setFrom(firstOfMonth()); setTo(today()); }
  };

  const v = data?.visits;
  const o = data?.orders;

  return (
    <>
      <PageHeader title={data ? data.rep.name : 'Salesman dashboard'} />
      <div className="col gap-16">
        <div className="card card-pad">
          <div className="row gap-12 wrap" style={{ alignItems: 'flex-end' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/team')}>
              ← Back to team
            </button>
            {data?.rep.managerName ? (
              <span className="small faint">Reports to {data.rep.managerName}</span>
            ) : null}
            <div className="grow" />
            <div className="row gap-8">
              {(['today', 'month', 'custom'] as Preset[]).map((p) => (
                <button key={p} type="button"
                  className={`btn btn-sm ${preset === p ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => applyPreset(p)}>
                  {p === 'today' ? 'Today' : p === 'month' ? 'This month' : 'Custom'}
                </button>
              ))}
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>From</label>
              <input type="date" className="input" value={from}
                onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>To</label>
              <input type="date" className="input" value={to}
                onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} />
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void run()}>Apply</button>
          </div>
        </div>

        {loading ? <PageLoading rows={5} /> : !data || !v ? (
          <Banner kind="info">No data for this range yet.</Banner>
        ) : (
          <>
            <div className="stat-row" style={{ marginBottom: 0 }}>
              <Stat icon="checklist" label="Visits" value={v.total.toLocaleString('en-IN')} sub={`${v.productive} productive`} />
              <Stat icon="check" label="Productive" value={v.productivePct != null ? `${v.productivePct}%` : '—'} sub="of visits" />
              <Stat icon="pin" label="Beat coverage"
                value={data.hasPlan && v.completionPct != null ? `${v.completionPct}%` : '—'}
                sub={data.hasPlan ? 'assigned → done' : 'plans off'} />
              <Stat icon="history" label="Avg time on site" value={mins(v.avgVisitMinutes)} sub="per call" />
            </div>

            <div className="stat-row" style={{ marginBottom: 0 }}>
              <Stat icon="checklist" label="Orders"
                value={data.ordersEnabled && o ? o.count.toLocaleString('en-IN') : '—'}
                sub={o ? `${o.lines} lines` : 'orders off'} />
              <Stat icon="check" label="Order value"
                value={data.ordersEnabled && o ? inr(o.valuePaise) : '—'}
                sub={o ? `avg ${inr(o.avgValuePaise)}` : ''} />
              <Stat icon="check" label="Strike rate"
                value={data.strikePct != null ? `${data.strikePct}%` : '—'} sub="orders ÷ productive" />
              <Stat icon="map" label="Distance"
                value={data.trackingEnabled && v.travelKm != null ? `${v.travelKm} km` : '—'}
                sub={data.trackingEnabled ? 'on the move' : 'tracking off'} />
            </div>

            <div className="row gap-16 wrap" style={{ alignItems: 'stretch' }}>
              <div className="card card-pad grow" style={{ minWidth: 320 }}>
                <div className="card-head" style={{ marginBottom: 12 }}>
                  <div>
                    <h3>Visits per day</h3>
                    <p className="small faint" style={{ margin: 0 }}>Productive against other calls.</p>
                  </div>
                  <div className="row gap-12" style={{ fontSize: 12.5 }}>
                    <span className="row gap-8" style={{ alignItems: 'center' }}>
                      <span style={{ width: 11, height: 11, borderRadius: 3, background: 'var(--brand)', display: 'inline-block' }} /> Productive
                    </span>
                    <span className="row gap-8" style={{ alignItems: 'center' }}>
                      <span style={{ width: 11, height: 11, borderRadius: 3, background: '#cbd2dc', display: 'inline-block' }} /> Other
                    </span>
                  </div>
                </div>
                <TrendChart trend={data.trend} />
              </div>

              <div className="card card-pad" style={{ minWidth: 260, flex: '0 0 300px' }}>
                <h3>Integrity flags</h3>
                <p className="small faint" style={{ marginTop: 2 }}>Verified, kept, surfaced.</p>
                <div className="col gap-8" style={{ marginTop: 10 }}>
                  <FlagRow label="Far from shop pin" value={data.flags.farFromCustomer} tone="danger" />
                  <FlagRow label="Under-20s visits" value={data.flags.shortVisits} tone="warning" />
                  <FlagRow label="Flagged visits" value={data.flags.flagged} tone="warning" />
                </div>
              </div>
            </div>

            {data.comparison ? (
              <div className="card card-pad">
                <div className="card-head" style={{ marginBottom: 12 }}>
                  <div>
                    <h3>vs {data.comparison.teamName ?? 'team'} average</h3>
                    <p className="small faint" style={{ margin: 0 }}>
                      This rep against {data.comparison.reps} peers under the same manager.
                    </p>
                  </div>
                </div>
                <div className="col gap-16">
                  <CompareBar label="Productive %" you={v.productivePct} team={data.comparison.productivePct} max={100} />
                  <CompareBar label="Strike rate" you={data.strikePct} team={data.comparison.strikePct} max={60} />
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function Stat({ icon, label, value, sub }: { icon: IconName; label: string; value: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat-icon"><Icon name={icon} size={17} /></div>
      <div style={{ minWidth: 0 }}>
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
        {sub ? <span className="stat-label" style={{ color: 'var(--ink-faint)' }}>{sub}</span> : null}
      </div>
    </div>
  );
}

function FlagRow({ label, value, tone }: { label: string; value: number; tone: 'danger' | 'warning' }) {
  const colour = tone === 'danger' ? 'var(--danger)' : 'var(--warning)';
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <span className="small">{label}</span>
      <span style={{ fontWeight: 800, color: value ? colour : 'var(--ink-faint)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function CompareBar({ label, you, team, max }: { label: string; you: number | null; team: number | null; max: number }) {
  const y = you ?? 0; const t = team ?? 0;
  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12.5, marginBottom: 5 }}>
        <span>{label}</span>
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{you != null ? `${you}%` : '—'}</strong>
      </div>
      <div style={{ position: 'relative', height: 9, borderRadius: 5, background: '#eef1f5', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(100, (y / max) * 100)}%`, background: 'var(--brand)', borderRadius: 5 }} />
        <div style={{ position: 'absolute', top: -3, left: `${Math.min(100, (t / max) * 100)}%`, width: 2, height: 15, background: 'var(--ink)' }} />
      </div>
      <div className="small faint" style={{ marginTop: 4 }}>Team avg {team != null ? `${team}%` : '—'}</div>
    </div>
  );
}

function TrendChart({ trend }: { trend: RepTrendPoint[] }) {
  const W = 760; const H = 180; const padL = 28; const padB = 22; const padT = 8;
  const innerW = W - padL - 8; const innerH = H - padT - padB;
  const max = useMemo(() => Math.max(1, ...trend.map((p) => p.productive + p.other)), [trend]);
  if (trend.length === 0) return <p className="faint small">Nothing in this range.</p>;
  const n = trend.length; const slot = innerW / n;
  const barW = Math.max(3, Math.min(20, slot * 0.6));
  const labelEvery = Math.ceil(n / 8);
  const yFor = (val: number) => padT + innerH - (val / max) * innerH;
  const base = yFor(0);
  return (
    <div className="table-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, fontVariantNumeric: 'tabular-nums' }}
        role="img" aria-label="Visits per day, productive versus other">
        {[0, 0.5, 1].map((tk) => {
          const yy = padT + innerH - tk * innerH;
          return (
            <g key={tk}>
              <line x1={padL} y1={yy} x2={W - 8} y2={yy} stroke="#eef1f5" />
              <text x={0} y={yy + 3.5} fontSize="10" fill="#9ca3af">{Math.round(max * tk)}</text>
            </g>
          );
        })}
        {trend.map((p, i) => {
          const cx = padL + i * slot + slot / 2;
          const tot = p.productive + p.other;
          const label = i % labelEvery === 0 ? p.date.slice(8) : null;
          return (
            <g key={p.date}>
              {tot > 0 ? <rect x={cx - barW / 2} y={yFor(tot)} width={barW} height={base - yFor(tot)} rx={2} fill="#cbd2dc" /> : null}
              {p.productive > 0 ? <rect x={cx - barW / 2} y={yFor(p.productive)} width={barW} height={base - yFor(p.productive)} rx={2} fill="var(--brand)" /> : null}
              {label ? <text x={cx} y={H - 6} fontSize="10" fill="#9ca3af" textAnchor="middle">{label}</text> : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
